import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import styles from './updateBasket.module.scss';
import BasketTabs from './components/BasketTabs/BasketTabs';
import InitialInputsContent from './tabs/InitialInputsContent';
import ParametersContent from './tabs/ParametersContent';
import AdditionalParametersContent from './tabs/AdditionalParametersContent';
import PreviewSummaryModal from './components/PreviewSummaryModal/PreviewSummaryModal';
import BasketErrorsModal from './components/BasketErrorsModal/BasketErrorsModal';
import SaveSuccessModal from './SaveSuccessModal';
import {
  checkTickersExistence,
  showTickersExistenceError,
  getBaskets,
  updateWithholdingTaxTemplatesData,
  createDataToShowOnSummary,
  transformErrorsForModal,
  updateSubmitBulkBasket,
  createSubmitBulkBasket,
  handleDownloadBulkTemplate,
  handleEditBaskets,
  handleCalculationZones,
  sortMetadataByCoverage,
} from '../../utils';
import {
  validateFieldValue,
  validateBasketsForm,
  getBasketsWithoutShares,
  getBasketsWithoutCoverageInstruments,
} from '../../utils/validations';
import BasketTable from './components/BasketTable';
import {
  TAB_ORDER,
  PREVIOUS,
  NEXT,
  INITIAL_INPUTS,
  PARAMETERS,
  ADDITIONAL_PARAMETERS,
  TYPE_OF_MODIFICATION,
  MODULE_TO_TAB_MAP,
  FIELDS_TO_PROPERTIES_MAP,
  CALCULATION_ZONE_FOR_MARKET_VECTOR,
  EASTERN_APAC_NAME,
} from '../../utils/constants';
import { ORGANIZATION_PREFIXES } from '../../../../constants/organizationPrefixConstants';
import { FieldRefProvider, useFieldRefContext } from '../../../../bitaHooks';
import {
  hasValidBitaDomain,
  hasValidMarketVectorOrgAll,
  hasValidUBSAPACOnly,
} from '../../../../utils/utils';

const UpdateBulkBasketInner = ({ isSubmitBasket = false }) => {
  const { focusFieldByProperty } = useFieldRefContext();
  const user = useSelector(state => state.auth);
  const token = user?.token;
  const authOrgPrefix = user?.org_prefix;
  const [errors, setErrors] = useState({});
  const [selectedTab, setSelectedTab] = useState(INITIAL_INPUTS);
  const [checkedBasketsIds, setCheckedBasketsIds] = useState([]);
  const [selectedBasketsData, setSelectedBasketsData] = useState([]);
  const [allBasketsList, setAllBasketsList] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [showPreviewSummaryModal, setShowPreviewSummaryModal] = useState(false);
  const [isLoadingBasketData, setIsLoadingBasketData] = useState(false);
  const [isLoadingDownloadTemplate, setIsLoadingDownloadTemplate] = useState(false);
  const [basketsWithoutCoverage, setBasketsWithoutCoverage] = useState([]);
  const [showSaveSuccessModal, setShowSaveSuccessModal] = useState(false);
  const [processedBaskets, setProcessedBaskets] = useState([]);
  const [withholdingTaxTemplateData, setWithholdingTaxTemplateData] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [shouldSendLaunchDate, setShouldSendLaunchDate] = useState(true);
  const [enableDivisorOrStartingValue, setEnableDivisorOrStartingValue] = useState({});
  const [dataToShowOnSummary, setDataToShowOnSummary] = useState([]);
  const [previousData, setPreviousData] = useState([]);
  const [showBasketErrorsModal, setShowBasketErrorsModal] = useState(false);
  const [errorsModalData, setErrorsModalData] = useState({});
  const [resetEditTablePageTrigger, setResetEditTablePageTrigger] = useState(0);
  const isUBSUser = authOrgPrefix === ORGANIZATION_PREFIXES.UBS;
  const [calculationZoneList, setCalculationZoneList] = useState([]);

  const sliderBaskets = useMemo(() => {
    return selectedBasketsData?.map(index => ({
      symbol: index.symbol,
      hasAnError:
        (errors && Object.keys(errors[index.symbol] ?? {}).length > 0) ||
        (basketsWithoutCoverage && basketsWithoutCoverage.includes(index.symbol)),
    }));
  }, [errors, basketsWithoutCoverage]);

  const handleStepsButtons = step => {
    const currentIndex = TAB_ORDER.indexOf(selectedTab);

    if (step === PREVIOUS && currentIndex > 0) {
      setSelectedTab(TAB_ORDER[currentIndex - 1]);
    } else if (step === NEXT && currentIndex < TAB_ORDER.length - 1) {
      setSelectedTab(TAB_ORDER[currentIndex + 1]);
    }
  };

  const resetValues = () => {
    setSelectedBasketsData([]);
    setAllBasketsList([]);
    setSelectedIndex(null);
    setShowPreviewSummaryModal(false);
    setIsLoadingBasketData(false);
    setIsLoadingDownloadTemplate(false);
    setBasketsWithoutCoverage([]);
    setShowSaveSuccessModal(false);
    setProcessedBaskets([]);
    setUploadedFiles([]);
    setErrors({});
    setCheckedBasketsIds([]);
    setSelectedTab(TAB_ORDER[0]);
    getBaskets(token, setAllBasketsList);
  };

  useEffect(() => {
    resetValues();
    if (isSubmitBasket) return;
    getBaskets(token, setAllBasketsList);
  }, [isSubmitBasket]);

  useEffect(() => {
    updateWithholdingTaxTemplatesData(user, setWithholdingTaxTemplateData);
  }, []);

  const existsCashConstituentsInMetadata = symbol =>
    selectedBasketsData
      ?.find(basket => basket?.symbol === symbol)
      ?.meta_reference_data?.some(instrument => instrument?.exchange === 'CASH') || false;

  const handleUpdateSubmit = async () => {
    const processedBasketsList = await updateSubmitBulkBasket({
      selectedBasketsData,
      existsCashConstituentsInMetadata,
      token: user.token,
    });
    if (processedBasketsList) {
      setProcessedBaskets(processedBasketsList);
      setShowSaveSuccessModal(true);
      setSelectedIndex(null);
      setUploadedFiles([]);
    }
    setShowPreviewSummaryModal(false);
  };

  const handleCreateSubmit = async () => {
    const processedBasketsList = await createSubmitBulkBasket({
      selectedBasketsData,
      enableDivisorOrStartingValue,
      shouldSendLaunchDate,
      existsCashConstituentsInMetadata,
      token: user.token,
    });
    if (processedBasketsList) {
      setProcessedBaskets(processedBasketsList);
      setShowSaveSuccessModal(true);
      setSelectedIndex(null);
      setUploadedFiles([]);
    }
    setShowPreviewSummaryModal(false);
  };

  const handleDownloadTemplate = async () => {
    await handleDownloadBulkTemplate({
      allBasketsList,
      checkedBasketsIds,
      token: user.token,
      setIsLoadingDownloadTemplate,
      setCheckedBasketsIds,
    });
  };

  const basketsWithoutShares = useMemo(
    () => getBasketsWithoutShares(selectedBasketsData, isUBSUser),
    [selectedBasketsData, isUBSUser],
  );

  const basketsWithoutCoverageInstruments = useMemo(
    () => getBasketsWithoutCoverageInstruments(selectedBasketsData, isUBSUser),
    [selectedBasketsData, isUBSUser],
  );

  const errorsCount = useMemo(() => {
    return Object.values(errors).flatMap(errObj => Object.values(errObj)).length;
  }, [errors]);

  // Validation function using the new organized validation system
  const validateForm = useCallback(
    (basketList, isUserUploadingData = false) => {
      const filteredErrors = validateBasketsForm({
        basketList,
        basketsWithoutShares,
        basketsWithoutCoverageInstruments,
        authOrgPrefix,
        isSubmitBasket,
        enableDivisorOrStartingValue,
        shouldSendLaunchDate,
        existsCashConstituentsInMetadata,
        isUserUploadingData,
      });
      setErrors(filteredErrors);
      setErrorsModalData(transformErrorsForModal(filteredErrors));
      return Object.keys(filteredErrors).length > 0;
    },
    [
      selectedBasketsData,
      basketsWithoutShares,
      basketsWithoutCoverageInstruments,
      authOrgPrefix,
      isSubmitBasket,
      enableDivisorOrStartingValue,
      shouldSendLaunchDate,
      existsCashConstituentsInMetadata,
    ],
  );

  useEffect(() => {
    validateForm(selectedBasketsData || []);
    if (selectedIndex === null && selectedBasketsData.length > 0) {
      setSelectedIndex(selectedBasketsData.length > 0 ? selectedBasketsData[0].symbol : null);
    }
    const filteredBasketsWithoutCoverage = (selectedBasketsData || [])
      .filter(basket =>
        (basket?.meta_reference_data || []).some(
          item => item?.type !== TYPE_OF_MODIFICATION.WITH_COVERAGE,
        ),
      )
      .map(basket => basket.symbol);
    setBasketsWithoutCoverage(prev => [...prev, ...filteredBasketsWithoutCoverage]);
  }, [selectedBasketsData, enableDivisorOrStartingValue]);

  const handleCreateSummaryData = () => {
    // Ensure selectedBasketsData and previousData are arrays
    if (!Array.isArray(selectedBasketsData) || !Array.isArray(previousData)) {
      setDataToShowOnSummary([]);
      return;
    }
    const allResults = createDataToShowOnSummary(selectedBasketsData, previousData, authOrgPrefix);
    setDataToShowOnSummary(Array.isArray(allResults) ? [...allResults] : []);
  };

  const handleOpenPreviewSummaryModal = async () => {
    handleCreateSummaryData();
    if (isSubmitBasket && selectedBasketsData && selectedBasketsData.length > 0) {
      const existingTickers = await checkTickersExistence({
        baskets: selectedBasketsData,
        token,
      });
      if (existingTickers.length > 0) {
        showTickersExistenceError(existingTickers);
        return;
      }
    }
    setShowPreviewSummaryModal(true);
  };

  const handleGoFix = (module, property) => {
    // Close the errors modal
    setShowBasketErrorsModal(false);

    // Ensure index metadata is sorted by coverage before navigating to fix
    setSelectedBasketsData(prev =>
      prev.map(basket => ({
        ...basket,
        meta_reference_data: sortMetadataByCoverage(basket?.meta_reference_data),
      })),
    );

    // Map module name to tab constant
    const targetTab = MODULE_TO_TAB_MAP[module];
    if (targetTab) {
      setSelectedTab(targetTab);
      // When navigating to Initial Inputs (metadata table), reset table to page 1
      if (targetTab === INITIAL_INPUTS) {
        setResetEditTablePageTrigger(prev => prev + 1);
      }
    }

    // Wait for tab to render and then focus the field
    // Delay allows the tab content to mount and register refs
    // Increased delay for metadata table fields to ensure table is fully rendered
    const isMetadataTableField =
      property === FIELDS_TO_PROPERTIES_MAP.SHARES ||
      property === FIELDS_TO_PROPERTIES_MAP.COVERAGE;
    const delay = isMetadataTableField ? 500 : 300;
    setTimeout(() => {
      focusFieldByProperty(property, { scrollDelay: 300 });
    }, delay);
  };

  const handleEdit = async () => {
    const { updatedResults, hasDuplicates } = await handleEditBaskets({
      allBasketsList,
      checkedBasketsIds,
      selectedBasketsData,
      token,
      setIsLoadingBasketData,
      setCheckedBasketsIds,
    });
    if (hasDuplicates || !updatedResults) {
      return;
    }
    setSelectedBasketsData(prev => [...prev, ...updatedResults]);
    setPreviousData(prev => [...prev, ...updatedResults]);
    validateForm([...selectedBasketsData, ...updatedResults], true);
  };

  const calculationZones = async () => {
    const data = await handleCalculationZones(token);
    setCalculationZoneList(data);
  };

  const calculationZoneMap = useMemo(() => {
    if (!Array.isArray(calculationZoneList)) return new Map();

    return new Map(calculationZoneList.map(zone => [zone.name, zone]));
  }, [calculationZoneList]);

  const calculationZoneOptions = useMemo(() => {
    if (!Array.isArray(calculationZoneList)) return [];

    return calculationZoneList.map(zone => ({
      id: zone.name,
      value: zone.public_name ?? zone.name,
    }));
  }, [calculationZoneList]);

  const filteredCalculationZones = useMemo(() => {
    if (!calculationZoneOptions.length) return [];

    const companyId = user?.userdata?.id_company;
    const email = user?.userdata?.email;

    const isSpecialUser = hasValidUBSAPACOnly(companyId) || hasValidBitaDomain(email);
    const isMarketVectorAll = hasValidMarketVectorOrgAll(companyId);
    const extraMarketVectorCalculationZones = CALCULATION_ZONE_FOR_MARKET_VECTOR;

    return calculationZoneOptions.filter(item => {
      if (item.id === EASTERN_APAC_NAME && !isSpecialUser) return false;
      if (!isMarketVectorAll && extraMarketVectorCalculationZones.includes(item.id)) return false;
      return true;
    });
  }, [calculationZoneOptions, user]);

  useEffect(() => {
    calculationZones();
  }, []);

  return (
    <>
      <div className={styles.content}>
        <div>
          {!isSubmitBasket && (
            <BasketTable
              allBasketsList={allBasketsList}
              setCheckedBasketsIds={setCheckedBasketsIds}
              handleClearSelection={() => setCheckedBasketsIds([])}
              checkedBasketsIds={checkedBasketsIds}
              handleDownloadTemplate={handleDownloadTemplate}
              isLoadingDownloadTemplate={isLoadingDownloadTemplate}
              handleEdit={handleEdit}
              isLoadingBasketData={isLoadingBasketData}
            />
          )}
          <div className={styles.pageContainer}>
            <div className={styles.sectionTitle}>
              {isSubmitBasket ? 'NEW BASKETS SUBMISSION' : 'BASKET UPDATE SUBMISSION'}
            </div>
            <BasketTabs
              selectedTab={selectedTab}
              onTabChange={setSelectedTab}
              shouldBeDisabled={selectedBasketsData.length === 0}
            />
            <div>
              {selectedTab === INITIAL_INPUTS && (
                <InitialInputsContent
                  validateForm={validateForm}
                  errors={errors}
                  isUBSUser={isUBSUser}
                  enableDivisorOrStartingValue={enableDivisorOrStartingValue}
                  setEnableDivisorOrStartingValue={setEnableDivisorOrStartingValue}
                  sliderBaskets={sliderBaskets}
                  allBasketsList={allBasketsList}
                  addData={value => setSelectedBasketsData(prev => [...prev, ...value])}
                  selectedIndex={selectedIndex}
                  selectedBasketsData={selectedBasketsData}
                  setSelectedIndex={setSelectedIndex}
                  setSelectedBasketsData={setSelectedBasketsData}
                  setBasketsWithoutCoverage={setBasketsWithoutCoverage}
                  setUploadedFiles={setUploadedFiles}
                  uploadedFiles={uploadedFiles}
                  calculationZoneMap={calculationZoneMap}
                  isSubmitBasket={isSubmitBasket}
                  addPreviousData={value => setPreviousData(prev => [...prev, ...value])}
                  resetEditTablePageTrigger={resetEditTablePageTrigger}
                />
              )}
              {selectedTab === PARAMETERS && (
                <ParametersContent
                  setBasketsWithoutCoverage={setBasketsWithoutCoverage}
                  shouldSendLaunchDate={shouldSendLaunchDate}
                  setShouldSendLaunchDate={setShouldSendLaunchDate}
                  isSubmitBasket={isSubmitBasket}
                  sliderBaskets={sliderBaskets}
                  setWithholdingTaxTemplateData={setWithholdingTaxTemplateData}
                  withholdingTaxTemplateData={withholdingTaxTemplateData}
                  existsCashConstituentsInMetadata={existsCashConstituentsInMetadata}
                  errors={errors}
                  selectedIndex={selectedIndex}
                  setSelectedIndex={setSelectedIndex}
                  selectedBasketsData={selectedBasketsData ?? []}
                  handleUpdateProperty={setSelectedBasketsData}
                  validateFields={validateFieldValue}
                  enableDivisorOrStartingValue={enableDivisorOrStartingValue}
                  setEnableDivisorOrStartingValue={setEnableDivisorOrStartingValue}
                  calculationZoneOptions={filteredCalculationZones}
                  calculationZoneMap={calculationZoneMap}
                />
              )}
              {selectedTab === ADDITIONAL_PARAMETERS && (
                <AdditionalParametersContent
                  errors={errors}
                  sliderBaskets={sliderBaskets}
                  selectedBasketsData={selectedBasketsData ?? []}
                  selectedIndex={selectedIndex}
                  setSelectedIndex={setSelectedIndex}
                  handleUpdateProperty={setSelectedBasketsData}
                  addData={value => setSelectedBasketsData(prev => [...prev, ...value])}
                />
              )}
            </div>
            <div className={styles.contentButtonSubmitCancel}>
              <button
                disabled={selectedTab === INITIAL_INPUTS}
                className={`${styles.previousNextButton} ${
                  selectedTab === INITIAL_INPUTS ? styles.disabledButton : ''
                }`}
                onClick={() => handleStepsButtons(PREVIOUS)}
              >
                {'<< Previous Step'}
              </button>
              <button
                disabled={
                  selectedBasketsData.length === 0 ||
                  basketsWithoutCoverage.length > 0 ||
                  Object.keys(errors).length > 0
                }
                className={styles.submitButton}
                onClick={handleOpenPreviewSummaryModal}
              >
                Submit
              </button>
              <button
                disabled={selectedTab === ADDITIONAL_PARAMETERS || selectedBasketsData.length === 0}
                className={`${styles.previousNextButton} ${
                  selectedTab === ADDITIONAL_PARAMETERS ? styles.disabledButton : ''
                }`}
                onClick={() => handleStepsButtons(NEXT)}
              >
                {'Next Step >>'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {showPreviewSummaryModal && (
        <PreviewSummaryModal
          isSubmitBasket={isSubmitBasket}
          existsCashConstituentsInMetadata={existsCashConstituentsInMetadata}
          withholdingTaxTemplateData={withholdingTaxTemplateData}
          selectedBasketsData={selectedBasketsData ?? []}
          updatedBasketsData={dataToShowOnSummary ?? []}
          previousBasketsData={previousData ?? []}
          showModal={showPreviewSummaryModal}
          closeModal={() => setShowPreviewSummaryModal(false)}
          onConfirm={isSubmitBasket ? handleCreateSubmit : handleUpdateSubmit}
          calculationZoneMap={calculationZoneMap}
        />
      )}
      <SaveSuccessModal
        showModal={showSaveSuccessModal}
        setShowModal={setShowSaveSuccessModal}
        resetValues={resetValues}
        processedBaskets={processedBaskets}
      />
      <BasketErrorsModal
        errorsCount={errorsCount}
        showModal={showBasketErrorsModal}
        closeModal={() => setShowBasketErrorsModal(false)}
        errorsModalData={errorsModalData[selectedIndex]}
        sliderBaskets={sliderBaskets?.filter(index => index.hasAnError)}
        selectedIndex={selectedIndex}
        setSelectedIndex={setSelectedIndex}
        onGoFix={handleGoFix}
      />
      {Object.keys(errors).length > 0 && (
        <button className={styles.floatingButton} onClick={() => setShowBasketErrorsModal(true)}>
          Basket Errors
        </button>
      )}
    </>
  );
};

const UpdateBulkBasket = ({ isSubmitBasket = false }) => {
  return (
    <FieldRefProvider>
      <UpdateBulkBasketInner isSubmitBasket={isSubmitBasket} />
    </FieldRefProvider>
  );
};

export default UpdateBulkBasket;
